import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { supportServiceTagApiClient } from '@src/api/tenant/supportService/supportServiceTagApiClient';
import { Pagination } from '@src/api/types/pagination';
import { SupportServiceTag } from '@src/api/types/supportServiceTag';
import CreateEditTagModal from '@src/components/CreateEditTagModal';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import TagTable from '@src/components/tagTable/TagTable';
import { useQueryParams } from '@src/hooks/useQueryParams';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import Title from '@src/ui/elements/typography/Title';
import logger from '@src/utils/logger';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const SupportServiceTagListForm: React.FC = () => {
  const [tags, setTags] = useState<SupportServiceTag[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [pagination, setPagination] = useState<Pagination>({} as Pagination);
  const [selectedTag, setSelectedTag] = useState<Partial<SupportServiceTag> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const {
    searchValue,
    setSearchValue,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    getSearchParams,
    sort,
    page,
    keyword,
    direction,
  } = useQueryParams({});

  useEffect(() => {
    if (!sort) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('sort', 'id');
      newSearchParams.set('direction', 'desc');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [sort, searchParams, setSearchParams]);

  const fetchTags = async () => {
    setLoading(true);
    const params = getSearchParams();

    const response = await supportServiceTagApiClient.index(params);
    handleApiResponse(
      response,
      (data) => {
        setTags(data.data);
        setPagination(data.pagination);
      },
      (error) => {
        console.error('タグを取得できませんでした:', error);
        logger.error('タグを取得できませんでした:', error);
        setErrors(errors);
      },
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchTags();
  }, [sort, direction, page, keyword]);

  const handleCreateClick = () => {
    setSelectedTag(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (tag: SupportServiceTag) => {
    setSelectedTag(tag);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTag(null);
    setErrors({});
  };

  const TagTableCallback = useCallback(() => {
    return (
      <TagTable
        items={tags}
        handleEditClick={handleEditClick}
        pagination={pagination}
        handlePageChange={handlePageChange}
      />
    );
  }, [tags, pagination]);

  const handleSave = async () => {
    if (selectedTag && selectedTag.id !== undefined) {
      const response = await supportServiceTagApiClient.update(selectedTag.id, selectedTag);
      handleApiResponse(
        response,
        () => {
          fetchTags();
          setErrors({});
          setIsModalOpen(false);
        },
        (error) => {
          setErrors(error);
          console.error('タグを更新できませんでした:', error);
          logger.error('タグを更新できませんでした:', error);
        },
      );
    } else {
      const response = await supportServiceTagApiClient.create(selectedTag as SupportServiceTag);
      handleApiResponse(
        response,
        () => {
          fetchTags();
          setErrors({});
          setIsModalOpen(false);
        },
        (error) => {
          setErrors(error);
          console.error('タグを作成できませんでした:', error);
          logger.error('タグを作成できませんでした:', error);
        },
      );
    }
  };
  return (
    <div className=''>
      {loading && <SpinnerOverlay loading={loading} />}
      <div className='flex items-center justify-between'>
        <Title>タグ一覧</Title>
        <div className='w-[250px]'>
          <InputSearch
            onSubmit={handleSearch}
            value={searchValue}
            defaultValue={keyword || ''}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onClear={() => {
              setSearchValue('');
              setSearchParams(new URLSearchParams());
            }}
          />
        </div>
      </div>
      <div className='mt-4 flex justify-end'>
        <Button
          size='s'
          onClick={handleCreateClick}
        >
          新規作成
        </Button>
      </div>
      {keyword && (
        <p className='overflow-wrap-anywhere mt-8 text-sm'>
          「{keyword}」で絞り込んだ結果：{pagination?.total}件
        </p>
      )}
      <div className='mt-8'>
        <TagTableCallback />
      </div>
      {!keyword && pagination?.total === 0 ? (
        <p className='mt-4 text-sm'>データがありません。</p>
      ) : null}
      <CreateEditTagModal
        isOpen={isModalOpen}
        tag={selectedTag}
        onCategoryChange={(updatedTag) => setSelectedTag(updatedTag as Partial<SupportServiceTag>)}
        errors={errors}
        onSave={handleSave}
        onCancel={handleCloseModal}
      />
    </div>
  );
};

export default SupportServiceTagListForm;
